import {
  Callout,
  Card,
  Classes,
  Icon,
  Intent,
  NonIdealState,
  Position,
  Spinner,
  Tag,
} from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { IRawTreeNodes, ISynthesis, MTree } from '@moleculeone/tree';
import { Button, styled } from '@moleculeone/ui';
import { Elements } from '@retro-ui/api-contract';
import {
  formatTime,
  LegacySearchDetailLevel,
  SearchDetailLevel,
  SearchResultStatus,
} from '@retro-ui/shared';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useParams } from 'react-router-dom';

import { apiQueryClient } from '@f/api-client';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import BatchSearchStatusTag from '@f/components/shared/batch-search-status-tag/BatchSearchStatusTag';
import { DownloadSynthesisPopover } from '@f/components/shared/download-synthesis-popover/DownloadSynthesisPopover';
import LoadingScreen from '@f/components/shared/loading-screen/LoadingScreen';
import ParameterList from '@f/components/shared/parameter-list/ParameterList';
import { SearchDetails } from '@f/components/shared/search-details/SearchDetails';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import { SciFinderAuthContext } from '@f/context';
import { useIsDebugModeOn } from '@f/hooks/useIsDebugModeOn';
import { addToReport } from '@f/services/batch-search/batch-search.actions';
import { sessionStorageClient } from '@f/services/storage/wrapper';
import { Exception } from '@f/types';
import { smilesToMol } from '@f/utils/conversion';

import { BatchSearchRequestName } from '../../../components/BatchSearchRequestName';
import { ContextualSynthesisTree } from '../components/ContextualSynthesisTree';
import { getInitialFilters } from '../components/filtering/SearchResultFilters';
import ReportTreePopover from '../components/ReportTreePopover';
import { ResultsNavigation } from '../components/ResultsNavigation';
import { RetrosynthesisButton } from '../components/RetrosynthesisButton';
import { buildResultQuery } from '../util/buildResultQuery';

import cls from './ResultPage.module.css';
import { parseFilters } from './SearchResultListPage';

const ADD_TO_REPORT_TOAST_KEY = 'add_single_to_report_toast';

const Flex = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const StyledToolbar = styled(Toolbar, {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const DebugButton = styled(Button, {
  fontFamily: '$mono',
});

export function ResultPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const isDebugModeOn = useIsDebugModeOn();
  const { accessToken } = useContext(SciFinderAuthContext);
  const params = useParams<any>();
  const { batchSearchId: searchId, batchSearchResultId: resultId } = params;

  const [treeNodes, setTreeNodes] = useState<IRawTreeNodes | null>(null);

  const { request, isAddingToReport } = useSelector((state: any) => {
    const requestState = state.batchSearch.request;
    const request = requestState.data[searchId];

    const resultState = state.batchSearch.result;

    return {
      request,
      isAddingToReport: resultState.addToReport.processing > 0,
    };
  });

  const queryKey = ['result', searchId, resultId];
  const resultQuery = apiQueryClient.search.fetchUniqueSearchResult.useQuery(queryKey, {
    params: {
      searchId,
      resultId,
    },
  });

  const isProcessingResult = resultQuery.isLoading;
  const result = resultQuery.data?.body;
  const queryError = resultQuery.error;
  const error = queryError?.body as Exception;

  const resultQueryParams = useMemo(() => {
    const filtersFromStorage = sessionStorageClient.getItem(`filters-${searchId}`);
    const searchFilters = filtersFromStorage
      ? parseFilters(filtersFromStorage)
      : getInitialFilters();
    return buildResultQuery(searchFilters, 1);
  }, [searchId]);

  const resultIdsQueryKey = useMemo(
    () => ['resultIds', resultId, searchId, resultQueryParams],
    [resultId, searchId, resultQueryParams]
  );
  const allResultsQuery = apiQueryClient.search.fetchAllSearchResults.useQuery(
    resultIdsQueryKey,
    {
      params: {
        searchId: searchId,
      },
      query: resultQueryParams,
    },
    {
      placeholderData: keepPreviousData,
      queryKey: resultIdsQueryKey,
    }
  );

  const isLoadingResults = allResultsQuery.isLoading;
  const allResults = useMemo(() => {
    return allResultsQuery.data?.body || [];
  }, [allResultsQuery.data]);
  const currentIndex = allResults.findIndex(item => item.id === resultId);

  const navigateToResult = useCallback(
    (index: number) => {
      if (index >= 0 && index < allResults.length) {
        history.push(`/dashboard/batch/${searchId}/result/${allResults[index].id}`);
      }
    },
    [allResults, history, searchId]
  );

  const goToPreviousResult = useCallback(() => {
    if (currentIndex > 0) {
      navigateToResult(currentIndex - 1);
    }
  }, [currentIndex, navigateToResult]);

  const goToNextResult = useCallback(() => {
    if (currentIndex < allResults.length - 1) {
      navigateToResult(currentIndex + 1);
    }
  }, [allResults.length, currentIndex, navigateToResult]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        navigateToResult(currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        navigateToResult(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex, navigateToResult]);

  useEffect(() => {
    async function generateTreeNodes(synthesis: ISynthesis) {
      const mtree = await MTree.parseSynthesis(synthesis, smilesToMol);
      const treeNodes = mtree.serialize();
      setTreeNodes(treeNodes);
    }
    if (result?.synthesis) {
      generateTreeNodes(result.synthesis as unknown as ISynthesis);
    }
  }, [result]);

  const invalidateResultQuery = () => {
    queryClient.invalidateQueries({ queryKey });
    resultQuery.refetch();
  };

  const addSynthesisToReport = async (
    reportId: string,
    elements: Elements,
    includeScifinderRelatedData: boolean
  ) => {
    AppToaster.show(
      {
        icon: <Spinner size={16} />,
        message: 'Adding to report...',
        timeout: 0,
        isCloseButtonShown: false,
      },
      ADD_TO_REPORT_TOAST_KEY
    );
    try {
      await dispatch(
        addToReport({
          reportId,
          elements,
          searchResultIds: [resultId],
          searchId,
          scifinderToken: accessToken,
          includeScifinderRelatedData,
        })
      );
      AppToaster.show(
        {
          intent: Intent.SUCCESS,
          message: 'Done!',
          icon: 'tick',
        },
        ADD_TO_REPORT_TOAST_KEY
      );
    } catch (error) {
      AppToaster.show(
        {
          intent: Intent.DANGER,
          message: 'An error occurred while adding to report',
          icon: 'error',
        },
        ADD_TO_REPORT_TOAST_KEY
      );
    }
  };

  if (isProcessingResult || isLoadingResults) {
    return (
      <div className={cls.container}>
        <LoadingScreen caption="Loading search results..." />
      </div>
    );
  }

  const detailLevel = request.searchParams?.detail_level;

  const shouldShowSynthesis = [
    LegacySearchDetailLevel._LEGACY_SYNTHESIS,
    SearchDetailLevel.BEST_PATH,
    SearchDetailLevel.ALL_PATHS,
  ].includes(detailLevel);

  const hasReferenceCount = result?.referenceCount != null;
  const isRunning = result?.status === SearchResultStatus.RUNNING;

  return (
    <>
      <StyledToolbar>
        <Flex>
          <Toolbar.Item>
            <NavLink
              className={cx(Classes.BUTTON, Classes.MINIMAL)}
              to={`/dashboard/batch/${request.id}`}
            >
              <Icon icon="double-chevron-left" />
              <span className={Classes.BUTTON_TEXT}>Back to results</span>
            </NavLink>
          </Toolbar.Item>

          <Toolbar.Item>
            <BatchSearchStatusTag large status={request.status} removed={request.isRemoved} />
          </Toolbar.Item>

          <Toolbar.Item>
            <BatchSearchRequestName batch={request} copyable showButton />
          </Toolbar.Item>

          <Toolbar.Item light>
            <SearchDetails search={request} />
          </Toolbar.Item>

          {isDebugModeOn && (
            <Toolbar.Item>
              <DebugButton
                text="View search config"
                size="small"
                weight="minimal"
                theme="purple"
                onClick={() => {
                  window.open(
                    `${window.location.origin}/internal/internal-retrosynthesis/search-config/${request.id}/${resultId}`,
                    '_blank'
                  );
                }}
              />
            </Toolbar.Item>
          )}

          {request.shareInfo.sharedWithMe && (
            <Toolbar.Item alignRight>
              <Tooltip2 content={`by ${request.shareInfo.ownerEmail}`} placement={Position.BOTTOM}>
                <Tag large intent={Intent.PRIMARY}>
                  Shared
                </Tag>
              </Tooltip2>
            </Toolbar.Item>
          )}
        </Flex>
        <ResultsNavigation
          goToPreviousResult={goToPreviousResult}
          goToNextResult={goToNextResult}
          currentIndex={currentIndex}
          resultsLength={allResults.length}
        />
      </StyledToolbar>

      {isRunning && (
        <div className={cls.container}>
          <LoadingScreen caption="Running..." />
        </div>
      )}

      {!isRunning && (
        <div className={cls.container}>
          {!queryError && result && (
            <div className={cx(cls.resultData, { [cls.withSynthesis]: shouldShowSynthesis })}>
              <Card className={cls.result}>
                <small className={cls.label}>RESULT</small>
                <ParameterList
                  className={cls.resultAtrributeList}
                  schema={[
                    {
                      label: 'Status',
                      renderer: data => {
                        switch (data.status) {
                          case SearchResultStatus.COMPLETED:
                            return <Icon icon="tick-circle" intent={Intent.SUCCESS} />;
                          case SearchResultStatus.FAILED:
                            return (
                              <Tag round intent={Intent.DANGER}>
                                Error
                              </Tag>
                            );
                          default:
                            return (
                              <Tag round intent={Intent.WARNING}>
                                {_.capitalize(data.status)}
                              </Tag>
                            );
                        }
                      },
                    },
                    {
                      label: 'Score',
                      renderer: data =>
                        data.result < 1 || _.isNaN(data.result) ? 'n/a' : data.result,
                    },
                    {
                      label: 'Reactions',
                      renderer: data => data.reactionCount ?? 'n/a',
                    },
                    ...(hasReferenceCount
                      ? [
                          {
                            label: 'References',
                            renderer: data => data.referenceCount ?? 'n/a',
                          },
                        ]
                      : []),
                    {
                      label: 'SM Price',
                      renderer: data =>
                        Number(data.price) > 0 ? `~ $${Number(data.price).toFixed(2)}` : 'n/a',
                    },
                    {
                      label: 'Started at',
                      renderer: data =>
                        data.startedAt ? new Date(data.startedAt).toLocaleString() : 'n/a',
                    },
                    {
                      label: 'Finished at',
                      renderer: data =>
                        data.finishedAt ? new Date(data.finishedAt).toLocaleString() : 'n/a',
                    },
                    {
                      label: 'Running time',
                      renderer: data => (data.runningTime ? formatTime(data.runningTime) : 'n/a'),
                    },
                    {
                      label: 'Name',
                      renderer: data => data.targetMetadata?.name ?? 'Unnamed',
                    },
                  ]}
                  data={result}
                />
                <div className={cls.additionalParameter}>
                  <span className={cls.additionalParameterName}>Target SMILES</span>
                  <p className={cls.additionalParameterValue}>{result.targetSmiles}</p>
                </div>

                {detailLevel === SearchDetailLevel.SCORE && (
                  <Callout className={cls.detailLevelScoreInfo} intent={Intent.PRIMARY}>
                    The synthesis pathway is not available for the detail level of{' '}
                    <code>score</code>. If you wish to see the synthesis pathway, use the detail
                    level of <code>all_paths</code> or <code>best_path</code> instead.
                  </Callout>
                )}
              </Card>

              {shouldShowSynthesis && (
                <Card className={cls.synthesis}>
                  <div className={cls.cardHeader}>
                    <small className={cls.label}>SYNTHESIS</small>
                    <div className={cls.cardHeaderActions}>
                      {result.synthesis && isDebugModeOn && (
                        <>
                          <DebugButton
                            text="View search graph"
                            size="small"
                            weight="minimal"
                            theme="purple"
                            onClick={() => {
                              window.open(
                                `${window.location.origin}/internal/internal-retrosynthesis/search-graph/${request.id}/${result.id}`,
                                '_blank'
                              );
                            }}
                          />
                          <DownloadSynthesisPopover
                            synthesis={result.synthesis as unknown as ISynthesis}
                          />
                        </>
                      )}
                      {result.synthesis && detailLevel === SearchDetailLevel.ALL_PATHS && (
                        <RetrosynthesisButton
                          searchId={request.id}
                          resultId={result.id}
                          sciFinderId={result.sciFinderId}
                          onCreatedSearch={invalidateResultQuery}
                          onFailed={invalidateResultQuery}
                        />
                      )}
                      {result.synthesis && (
                        <ReportTreePopover
                          canCreateReport
                          loading={isAddingToReport}
                          onAddToReport={addSynthesisToReport}
                          selectedResults={[result]}
                          searchName={request.name}
                        />
                      )}
                    </div>
                  </div>
                  <div className={cls.treeContainer}>
                    {!result.synthesis && (
                      <NonIdealState
                        title="No synthesis"
                        description={'There is no synthesis to show.'}
                        icon="warning-sign"
                      />
                    )}

                    {result.synthesis && !treeNodes && (
                      <LoadingScreen caption="Loading synthesis..." />
                    )}

                    {result.synthesis && treeNodes && (
                      <ContextualSynthesisTree
                        tree={treeNodes}
                        searchParams={request.searchParams?.presets}
                        synthesis={result.synthesis as unknown as ISynthesis}
                      />
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {queryError && error && (
            <NonIdealState
              title={error.statusCode}
              description={error.message}
              icon="warning-sign"
            />
          )}
          <Flex css={{ marginTop: '1rem' }}>
            <NavLink
              className={cx(Classes.BUTTON, Classes.MINIMAL)}
              to={`/dashboard/batch/${request.id}`}
            >
              <Icon icon="double-chevron-left" />
              <span className={Classes.BUTTON_TEXT}>Back to results</span>
            </NavLink>
            <ResultsNavigation
              goToPreviousResult={goToPreviousResult}
              goToNextResult={goToNextResult}
              currentIndex={currentIndex}
              resultsLength={allResults.length}
            />
          </Flex>
        </div>
      )}
    </>
  );
}
